import { isArrayLiteralExpression } from 'typescript';
import { v4 as uuidv4 } from 'uuid';
import {
  add,
  differenceInMonths,
  eachDayOfInterval,
  format,
  getDate,
  getDay,
  getDaysInMonth,
  getMonth,
  getYear,
  isSameMonth,
  parse,
  startOfDay,
  subMonths,
} from 'date-fns';
import { useSession } from '../hooks/session';
import { reactQueryClient } from '..';
import { KEY_READ_TEXT, KEY_WALK_TEXT } from './key';

export const STEP_TO_CALORIE_CONST = 0.03;
export const STEP_STRIDE_CONST = 60;
export const MAX_STEP = 999999999;

export const NORMAL_CHARACTER = 1;
export const FIXED_CHARACTER = 2;

export const BRAND_POINT_TYPE = '6/0';
export const NO_SELECT_ADVERTISER = 99;

// TOOL TIP LIST
export const TOOLTIP_FONT_SIZE = 14;

export const DATE_LOCALE_OFFSET = 9 * 60 * 60 * 1000;

export const educationOffices = [
  { name: '전체', seq: 0 },
  { name: '남부', seq: 1 },
  { name: '북부', seq: 2 },
  { name: '동부', seq: 3 },
  { name: '서부', seq: 4 },
  { name: '강화', seq: 5 },
];

export const schoolGroups = [
  { name: '초등학교', seq: 1 },
  { name: '중학교', seq: 2 },
  { name: '고등학교', seq: 3 },
];

export function getPostFixByType(type: string) {
  return type === KEY_READ_TEXT
    ? '권'
    : type === KEY_WALK_TEXT
    ? '걸음 이상'
    : '개';
}

export const GraphColors = [
  '#D796FF',
  '#30CBDF',
  '#2581FF',
  '#c7c7c8',
  '#FF976C',
  '#FF5881',
  '#FFCA58',
  '#00C0FF',
  '#1A237E',
  '#D7D8DC',
];

export function getSchoolGradeCount(schoolGroup: number) {
  return schoolGroup === 1 ? 6 : 3;
}

export function getSchoolClassCount() {
  return 20;
}

export function getReadGoal() {
  return ['1', '2', '3', '5'];
}

export function getWalkGoal() {
  return ['3000', '5000', '7000', '10000', '15000'];
}

export function getGender(gender: string) {
  return gender == null
    ? '-'
    : gender === 'M'
    ? '남성'
    : gender === 'F'
    ? '여성'
    : '-';
}

export function getTypeWord(statType: string) {
  return statType === KEY_READ_TEXT
    ? 'read'
    : statType === KEY_WALK_TEXT
    ? 'walk'
    : 'write';
}

export function getSchoolInSchoolGroup(
  newSchoolList: any[],
  newSchoolGroupSeq: number,
  newEducationOfficeSeq: number
) {
  if (
    newSchoolList[newSchoolGroupSeq] === undefined ||
    newSchoolList[newSchoolGroupSeq] === null
  )
    return [];
  if (newEducationOfficeSeq === 0) {
    return newSchoolList[newSchoolGroupSeq].sort((a: any, b: any) =>
      a.schoolName.localeCompare(b.schoolName)
    );
  }
  const newSchools = newSchoolList[newSchoolGroupSeq]
    .filter(
      (school: any, idx: number) =>
        school.educationOfficeSeq === newEducationOfficeSeq
    )
    .sort((a: any, b: any) => a.schoolName.localeCompare(b.schoolName));
  return newSchools;
}

// invalidateQueries : QueryClient 에서 제공하는 메서드로, 주어지는 키에 매칭되는 쿼리를 stale 한 상태로 만들어서 리패칭 하게 하는 메서드
export function queryInvalidate(key: string | string[]) {
  reactQueryClient.invalidateQueries(key);
}

export function convertDistance(distance: number) {
  const newDistance = Math.round(distance).toString();
  const contentLength = newDistance.length;
  let result: any;
  if (contentLength > 4) {
    const settingKm = Math.floor(distance / 1000);
    const settingMeter = (distance % 1000).toString().split('.')[0];
    result = `${settingKm}.${settingMeter}km`;
  } else if (contentLength === 4) {
    result = `${newDistance[0]},${newDistance.slice(1, 4)}m`;
  } else {
    result = `${newDistance}m`;
  }
  return result;
}

export function convertStep(distance: number) {
  return Math.round((distance * 100) / 60);
}

export function convertTime(time: number) {
  const newTime = Math.round(time);
  if (time / 60 >= 1) {
    const hour = Math.floor(newTime / 60);
    const minute = newTime % 60;
    return `${hour}시간${minute}분`;
  }
  if (newTime === 0) {
    const seconds = Math.round(time * 60);
    return `${seconds}초`;
  }
  return `${newTime}분`;
}

export function dateFormat(currentDate: Date, type: string) {
  const formattedDate = currentDate.toLocaleString(
    'ko-KR',
    type === 'date'
      ? {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        }
      : type === 'datepoint'
      ? {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
          hour12: true, // 24시간 형식
        }
      : {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
          second: 'numeric',
          hour12: false, // 24시간 형식
        }
  );

  if (type === 'datepoint') {
    const newDate = `${formattedDate.replace(':', '시 ')}분`;
    return newDate;
  }

  return formattedDate;
}

export function timestamp2string(timestamp: number, degree: number = 1) {
  return new Date(timestamp * degree)
    .toISOString()
    .replaceAll('T', ' ')
    .replaceAll('.000Z', '');
}

export function timestamp2Localestring(timestamp: number, degree: number = 1) {
  var d = new Date(timestamp * degree);
  var dateFormatStr = `${d.getFullYear().toString().padStart(2, '0')}-${(
    d.getMonth() + 1
  )
    .toString()
    .padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')} ${d
    .getHours()
    .toString()
    .padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}:${d
    .getSeconds()
    .toString()
    .padStart(2, '0')}`;

  return dateFormatStr;
}

export function generateUuidId() {
  return uuidv4().replace(/-/gi, '').toUpperCase();
}

export function getRegionCategory() {
  return [
    '경기도',
    '강원특별자치도',
    '경상남도',
    '경상북도',
    '광주광역시',
    '대구광역시',
    '대전광역시',
    '부산광역시',
    '서울특별시',
    '세종특별자치시',
    '울산광역시',
    '인천광역시',
    '전라남도',
    '전북특별자치도',
    '제주특별자치도',
    '충청남도',
    '충청북도',
  ];
}

export function getRegionAmdCode(amdCode: string) {
  const amdCodeList = [
    {
      do: '경기도',
      amdCode: '41',
    },
    { do: '강원특별자치도', amdCode: '51' },
    { do: '경상남도', amdCode: '48' },
    { do: '경상북도', amdCode: '47' },
    { do: '광주광역시', amdCode: '29' },
    { do: '대구광역시', amdCode: '27' },
    { do: '대전광역시', amdCode: '30' },
    { do: '부산광역시', amdCode: '26' },
    { do: '서울특별시', amdCode: '11' },
    { do: '세종특별자치시', amdCode: '36' },
    { do: '울산광역시', amdCode: '31' },
    { do: '인천광역시', amdCode: '28' },
    { do: '전라남도', amdCode: '46' },
    { do: '전북특별자치도', amdCode: '52' },
    { do: '제주특별자치도', amdCode: '50' },
    { do: '충청남도', amdCode: '44' },
    { do: '충청북도', amdCode: '43' },
  ];

  if (amdCode.length !== 2) {
    const found = amdCodeList.filter(
      (amd: any) => amdCode.substring(0, 2) === amd.amdCode
    );
    return found === undefined || found === null ? '' : found[0].do;
  }
  return '';
}

export function splitAddress(address: string) {
  if (address === null || address.length === 0) return '-';
  const newAddress = address.split(' ');

  let simpleAddress = '';
  if (newAddress.length > 2) {
    simpleAddress = newAddress[1].endsWith('시')
      ? `${newAddress[0]} ${newAddress[1]} ${newAddress[2]}`
      : newAddress[1].endsWith('구')
      ? `${newAddress[0]} ${newAddress[1]}`
      : newAddress[1].endsWith('군')
      ? `${newAddress[0]} ${newAddress[1]} ${newAddress[2]}`
      : '-';
  }

  return simpleAddress;
}

export function getCurrentDate() {
  const currentYear = new Date(Date.now()).getFullYear();
  const currentMonth = new Date(Date.now()).getMonth() + 1;
  const currentDay = new Date(Date.now()).getDate();
  return `${currentYear}-${`00${currentMonth}`.slice(
    -2
  )}-${`00${currentDay}`.slice(-2)}`;
}

export function getYearList() {
  var arrYear: string[] = [];
  const endYear = new Date(Date.now()).getFullYear();
  for (var i = 2020; i <= endYear; i += 1) {
    arrYear.push(`${i}년`);
  }

  return arrYear;
}

export function getMonthList() {
  var arrMonth: string[] = [];
  for (var i = 1; i <= 12; i += 1) {
    arrMonth.push(`${i}월`);
  }

  return arrMonth;
}

export function getMonthListByMax(max: number) {
  const result = [];
  for (let i = 1; i <= max; i += 1) {
    result.push(i.toString().padStart(2, '0'));
  }
  return result;
}

export const MobileRegexp = /^01([0|1|6|7|8|9])-?([0-9]{3,4})-?([0-9]{4})$/;

export function commaFormat(str: any) {
  return String(str).replace(/(\d)(?=(?:\d{3})+(?!\d))/g, '$1,');
}

export function checkImageFileName(str: string) {
  const nonImageExt = [
    'pdf',
    'hwp',
    'hwt',
    'sty',
    'xlsx',
    'xlsm',
    'xlsb',
    'xls',
    'csv',
    'xml',
    'xltx',
    'xltm',
    'xlt',
    'xls',
    'xlam',
    'xps',
    'ods',
    'pptx',
    'pptm',
    'ppt',
    'potx',
    'potm',
    'ppsx',
    'ppsm',
    'pps',
    'ppam',
    'ppa',
    'docx',
    'docm',
    'doc',
    'dotx',
    'dotm',
  ];
  var ext = str.split('.').pop()?.toLocaleLowerCase();
  if (ext && nonImageExt.includes(ext) === false) {
    return true;
  }
  return false;
}

export function validCheck(type: string, str: string) {
  const pattern =
    type === 'email'
      ? /[a-zA-Z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,8}(.[a-z{2,8}])?/g
      : type === 'phone'
      ? /^([+]?[\s0-9]+)?(\d{3}|[(]?[0-9]+[)])?([-]?[\s]?[0-9])+$/i
      : '';
  const regex = new RegExp(pattern);
  return regex.test(str);
}

export function checkPasswordStrength(password: string) {
  // 정규표현식을 사용하여 각각 대문자, 소문자, 숫자, 특수문자의 포함 여부를 확인
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasNumber = /\d/.test(password);
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

  // 최소 8자 이상이고, 대문자, 소문자, 숫자, 특수문자가 모두 포함되어 있는 경우 true 반환
  return (
    password.length >= 8 &&
    hasUpperCase &&
    hasLowerCase &&
    hasNumber &&
    hasSpecialChar
  );
}

export function checkJoinCode(joinCode: string) {
  const regex = /^[A-Za-z0-9]+$/;
  if (joinCode === undefined || joinCode === null || joinCode.length === 0)
    return true;
  // 최소 4자 이상, 12자 미만이고, 대문자, 소문자, 숫자 포함되어 있는 경우 true 반환
  return joinCode.length <= 12 && regex.test(joinCode);
}

export function checkNameStrength(name: string) {
  // 최소 2글자 이상 입력, 숫자 및 자음만 입력 불가.
  const hasConsonant = /^[ㄱ-ㅎ]+$/.test(name);
  const hasNumber = /^\d+$/.test(name);
  const hasAlphabet = /^[a-zA-Z]+$/.test(name);
  const hasSpecialChar = /^[!@#$%^&*(),.?":{}|<>]+$/.test(name);
  // 최소 2글자 이하, 숫자 및 자음만으로 구성된 경우 false
  return (
    (name.length < 2 ||
      hasConsonant ||
      hasAlphabet ||
      hasNumber ||
      hasSpecialChar) !== true
  );
}

export function removeNatCodePhoneNumber(str: string) {
  if (str === undefined || str === null) return '';
  return str.replaceAll('+82', '0');
}

export function elapsedTime(
  startTime: number,
  endTime: number,
  degree: number = 1
) {
  const attendStartDate = new Date(startTime * degree);
  const attendEndDate = new Date(endTime * degree);
  const diff = attendEndDate.getTime() - attendStartDate.getTime();
  const elapsedSec = Math.floor((diff / 1000) % 60);
  const elapsedMin = Math.floor((diff / (1000 * 60)) % 60);
  const elapsedHour = Math.floor((diff / (1000 * 60 * 60)) % 24);
  return elapsedHour > 0
    ? `${elapsedHour}시${elapsedMin}분${elapsedSec}초`
    : `${elapsedMin}분${elapsedSec}초`;
}

export function parseGPXContent(xml: any, radius: any, type: number) {
  // 0 : json, 1: array
  const parser = new DOMParser();
  const result: any = [];
  const newXML = parser.parseFromString(xml, 'text/xml');
  const toJson = newXML.querySelectorAll('trkpt');
  console.log('toJson:', toJson);
  toJson.forEach((element: any, idx: number) => {
    // idx % 32 === 1 &&
    //   idx % 64 === 1 &&
    if (idx % 2 && idx % 4 === 1 && idx % 8 === 1 && idx % 16 === 1) {
      if (type === 0) {
        result.push({
          lat: element.attributes[0].value,
          lng: element.attributes[1].value,
          radius: radius,
        });
      } else {
        result.push([
          Number(element.attributes[0].value),
          Number(element.attributes[1].value),
        ]);
      }
    }
  });
  return result;
}

export function getAddUrl(url: string) {
  if (url && !url.startsWith('h')) {
    return `https://${url}`;
  }
  return url;
}

export function getAddUrlWithHttp(url: string) {
  const result = /^(https?):\/\//.test(url);
  if (result) return url;

  const result2 = /^(https?):\//;
  if (result2) return 'http://';

  return `http://${url}`;
}

export function checkUrl(url: string) {
  if (url === null || url === undefined) return '';
  return url === 'http://' ? '' : url;
}

export function encodeSearchWord(searchKey: string, searchWord: string) {
  var newSearchWord = '';

  if (
    searchWord === undefined ||
    searchWord === null ||
    searchWord.length === 0
  )
    return '';

  if (
    searchKey === 'phoneNumber' ||
    searchKey === 'memberPhoneNumber' ||
    searchKey === 'phonenum'
  ) {
    newSearchWord = searchWord.replaceAll('-', '').replaceAll(' ', '').trim();
  } else {
    const tmpSearchWord = Number.isInteger(
      Number(searchWord.replaceAll('-', '').trim())
    )
      ? searchWord.replaceAll('-', '').trim()
      : searchWord.trim();
    newSearchWord = encodeURI(tmpSearchWord);
  }

  // console.log('newSearchWord:', newSearchWord);
  return newSearchWord;
}

export function trimWhiteSpace(inputString: string) {
  var newSearchWord = '';

  if (
    inputString === undefined ||
    inputString === null ||
    inputString.length === 0
  )
    return '';

  newSearchWord = inputString.replaceAll('\t', ' ').trim();

  return newSearchWord;
}

export function phoneNumberHyphen(value: string) {
  if (value === undefined || value === null || value === '') {
    return '--';
  }

  value = value.replace(/[^0-9]/g, '').replaceAll('-', '');

  const result = [];
  var restNumber = '';

  // 지역번호와 나머지 번호로 나누기
  if (value.startsWith('02')) {
    // 서울 02 지역번호
    result.push(value.substring(0, 2));
    restNumber = value.substring(2);
  } else {
    // 나머지 3자리 지역번호
    // 0xx-yyyy-zzzz
    result.push(value.substring(0, 3));
    restNumber = value.substring(3);
  }

  if (restNumber.length === 7) {
    // 7자리만 남았을 때는 xxx-yyyy
    result.push(restNumber.substring(0, 3));
    result.push(restNumber.substring(3));
  } else {
    result.push(restNumber.substring(0, 4));
    result.push(restNumber.substring(4));
  }

  return result.filter((val: any) => val).join('-');
}

export function throwError(status: any) {
  const { logout } = useSession();
  if (status === 401) {
    logout();
  } else {
    throw new Error('http 에러');
  }
}

export function ageFormat(age: any[]) {
  if (age === undefined || age === null) return [];
  const tmpAge = age.sort((a, b) => a - b);
  var newAge: any[] = [];

  if (tmpAge.length === 0) return [];
  // console.log('tmpAge: ', tmpAge);
  newAge.push(tmpAge[0]);
  let pushFlag = 0;
  for (let i = 1; i < tmpAge.length; i += 1) {
    if (Number(tmpAge[i - 1]) + 1 === Number(tmpAge[i])) {
      if (
        pushFlag === 0 &&
        Number(newAge[newAge.length - 1]) + 1 === Number(tmpAge[i])
      ) {
        newAge.push('~');
        newAge.push(tmpAge[i]);
        pushFlag = 1;
      } else {
        newAge[newAge.length - 1] = tmpAge[i];
      }
    } else {
      newAge.push(tmpAge[i]);
      pushFlag = 0;
    }
  }
  console.log('format newAge:', newAge);
  return newAge;
}

export function ageUnFormat(age: any[]) {
  console.log('age: ', age);
  if (age === undefined || age === null) return [];

  var newAge: any[] = [];

  for (let i = 0; i < age.length; i += 1) {
    if (age[i - 1] === '~') {
      console.log(
        'Number(age[i - 2]) + 1:',
        Number(age[i - 2]) + 1,
        ',  Number(age[i]):',
        Number(age[i])
      );
      for (let j = Number(age[i - 2]) + 1; j <= Number(age[i]); j += 1) {
        newAge.push(j);
      }
    } else if (age[i] !== '~') {
      newAge.push(age[i]);
    }
  }
  console.log('unformat newAge:', newAge);
  return newAge;
}

export function splitLocaitonLimit(location: string) {
  const newLocation = location.split(' ');
  const newSi =
    newLocation.length > 1 ? newLocation[1].replace('시', '시 ') : '';

  return newLocation.length > 1 ? `${newLocation[0]} ${newSi}` : location;
}

export function getCurrentStartDate() {
  const currentDate = new Date();
  const startYear = getYear(currentDate);
  const startMonth = getMonth(currentDate);
  const newStartDate = startOfDay(
    new Date(
      `${startYear}-${
        startMonth + 1 >= 10 ? startMonth + 1 : `0${startMonth + 1}`
      }-01`
    )
  );
  return Number(newStartDate) / 1000;
}

export function formatDateString(dateString: string, splitText: string) {
  if (
    dateString === undefined ||
    dateString === null ||
    dateString.length === 0
  )
    return '-';
  const year = dateString.substring(0, 4);
  const month = dateString.substring(4, 6);
  const day = dateString.substring(6, 8);
  return `${year}${splitText}${month}${splitText}${day}`;
}

export function getDateList(beforeMonth: number) {
  const currentDate = new Date();
  const pastDate = subMonths(new Date(currentDate), beforeMonth);
  const startYear = getYear(pastDate);
  const startMonth = getMonth(pastDate);
  const newStartDate = startOfDay(
    new Date(
      `${startYear}-${
        startMonth + 1 >= 10 ? startMonth + 1 : `0${startMonth + 1}`
      }-01`
    )
  );

  const dif = differenceInMonths(
    new Date(
      `${getYear(currentDate)}-${
        getMonth(currentDate) + 1 >= 10
          ? getMonth(currentDate) + 1
          : `0${getMonth(currentDate) + 1}`
      }-01`
    ),
    new Date(
      `${startYear}-${
        startMonth + 1 >= 10 ? startMonth + 1 : `0${startMonth + 1}`
      }-01`
    )
  );

  const newRateDate = new Array(dif + 1).fill(0).map((_d, idx) => {
    const newStart = Math.floor(
      new Date(
        add(newStartDate, {
          months: idx,
        })
      ).getTime() / 1000
    );
    return newStart;
  });
  return newRateDate.reverse();
}

export function getYearDateList(year: number) {
  const currentDate = new Date();
  const currentYear = getYear(currentDate);

  const startMonth = 0; // 기본적으로 1월부터 시작
  let endMonth = 11; // 기본적으로 12월까지

  if (year === currentYear) {
    endMonth = getMonth(currentDate); // 현재 연도라면 현재 월까지만
  }

  const result: number[] = [];
  for (let month = startMonth; month <= endMonth; month += 1) {
    const date = new Date(year, month, 1);
    result.push(Math.floor(date.getTime() / 1000));
  }

  return result.reverse();
}

export function getFirstMonthOfYear(loginVal: any) {
  const currentDate = new Date();
  const currentYear = getYear(currentDate);
  const targetYear = loginVal.value.user.year
    ? loginVal.value.user.year
    : currentYear;

  const newDataList = getYearDateList(targetYear);

  return newDataList[0];
}

export function firstDayOfMonth(currentDate: number) {
  return new Date(getYear(currentDate), getMonth(currentDate), 1);
}

export function lastDayOfMonth(currentDate: number) {
  return new Date(getYear(currentDate), getMonth(currentDate) + 1, 0);
}

export function getMonthDates(currentDate: number) {
  return eachDayOfInterval({
    start: firstDayOfMonth(currentDate),
    end: lastDayOfMonth(currentDate),
  });
}

export function getMonthText(date: number) {
  const startMonth = getMonth(date);
  return startMonth + 1 >= 10 ? startMonth + 1 : `0${startMonth + 1}`;
}

export function getDateText(date: number) {
  const startDay = getDate(date);
  return startDay >= 10 ? startDay : `0${startDay}`;
}

export function getWeekDates(year: number, weekNumber: number, offset = 0) {
  // offset : 0 : sunday start, 1: monday start
  const firstDayOfYear = new Date(year, 0, 1);
  const daysToFirstDayOfWeek = (weekNumber - 1) * 7;
  const firstDayOfWeek = new Date(year, 0, 1 + daysToFirstDayOfWeek + offset);
  const lastDayOfWeek = new Date(
    year,
    0,
    1 + daysToFirstDayOfWeek + 6 + offset
  );

  return {
    start: firstDayOfWeek,
    end: lastDayOfWeek,
  };
}

export function getCurriculumDration(
  year: number,
  weekNumber: number,
  idx: number
) {
  const duration = getWeekDates(year, weekNumber + idx, 0);

  const newDuration = `${duration.start
    .getFullYear()
    .toString()
    .substring(2, 4)}.${(duration.start.getMonth() + 1)
    .toString()
    .padStart(2, '0')}.${duration.start
    .getDate()
    .toString()
    .padStart(2, '0')}~${duration.end
    .getFullYear()
    .toString()
    .substring(2, 4)}.${(duration.end.getMonth() + 1)
    .toString()
    .padStart(2, '0')}.${duration.end.getDate().toString().padStart(2, '0')}`;
  return newDuration;
}

export function getWeekNumber(date: string) {
  const targetDate = new Date(date);

  const firstDayOfYear = new Date(targetDate.getFullYear(), 0, 1);
  const millisecondsPerWeek = 7 * 24 * 60 * 60 * 1000;
  const diff = Number(targetDate) - Number(firstDayOfYear);
  const weekNumber = Math.ceil((diff + 1) / millisecondsPerWeek);
  return weekNumber;
}

export function getDateRange(startDate: any, endDate: any, range: any) {
  const dateRange = [];
  const currentDate = new Date(startDate);
  const stopDate = new Date(endDate);

  switch (range) {
    case 'day':
      while (currentDate <= stopDate) {
        dateRange.push(new Date(currentDate));
        currentDate.setDate(currentDate.getDate() + 1);
      }
      break;

    case 'week':
      while (currentDate <= stopDate) {
        dateRange.push(new Date(currentDate));
        currentDate.setDate(currentDate.getDate() + 7);
      }
      break;

    case 'month':
      while (currentDate <= stopDate) {
        dateRange.push(new Date(currentDate));
        currentDate.setMonth(currentDate.getMonth() + 1);
      }
      break;

    default:
      throw new Error('invalid range.');
  }

  return dateRange;
}

const showRangeSample = () => {
  const startDate = '2023-02-01';
  const endDate = '2023-12-31';

  const dailyRange = getDateRange(startDate, endDate, 'day');
  console.log('일별:', dailyRange);

  const weeklyRange = getDateRange(startDate, endDate, 'week');
  console.log('주별:', weeklyRange);

  const monthlyRange = getDateRange(startDate, endDate, 'month');
  console.log('월별:', monthlyRange);
};

export function checkNaN(str: string, value: number) {
  return Number.isNaN(Number(str)) ? value : Number(str);
}

export function calculateDays(startDate: any, endDate: any) {
  const start = new Date(startDate);
  const end = new Date(endDate);

  // 시작 날짜와 끝 날짜 사이의 날짜 수 계산
  const differenceInTime = end.getTime() - start.getTime();
  const differenceInDays = differenceInTime / (1000 * 3600 * 24);

  return Math.round(differenceInDays);
}

export function getDayOfWeek(d: number) {
  switch (d) {
    case 0:
      return '일';
    case 1:
      return '월';
    case 2:
      return '화';
    case 3:
      return '수';
    case 4:
      return '목';
    case 5:
      return '금';
    case 6:
      return '토';
    default:
      return '?';
  }
}

export function getMonthStartEndText(targetDate: number) {
  const firstDay = firstDayOfMonth(targetDate);
  const lastDay = lastDayOfMonth(targetDate);
  // Format the Date object into the desired format
  const firstDayformattedDate = format(targetDate, 'yyyy/MM/dd');
  const lastDayformattedDate = format(lastDay, 'yyyy/MM/dd');
  return `${firstDayformattedDate}~${lastDayformattedDate}`;
}

export function convert2Date(dateString: string, timeString: string) {
  if (dateString === undefined || timeString === undefined) return '';
  // Combine date and time strings
  const dateTimeString = `${dateString} ${timeString}`;

  // Parse the combined string into a Date object
  const dateTime = parse(dateTimeString, 'yyyyMMdd HHmmss', new Date());

  // Format the Date object into the desired format
  const formattedDate = format(dateTime, 'yyyy/MM/dd(eee) a hh:mm');

  return formattedDate;
}

export function convertIphoneModel(model: string) {
  // 참조 : https://gist.github.com/adamawolf/3048717
  let newModel = '-';
  if (model.includes('iPhone1,1')) newModel = 'iPhone';
  else if (model.includes('iPhone1,2')) newModel = 'iPhone 3G';
  else if (model.includes('iPhone2,1')) newModel = 'iPhone 3GS';
  else if (model.includes('iPhone3,1')) newModel = 'iPhone 4';
  else if (model.includes('iPhone3,2')) newModel = 'iPhone 4 GSM Rev A';
  else if (model.includes('iPhone3,3')) newModel = 'iPhone 4 CDMA';
  else if (model.includes('iPhone4,1')) newModel = 'iPhone 4S';
  else if (model.includes('iPhone5,1')) newModel = 'iPhone 5 (GSM)';
  else if (model.includes('iPhone5,2')) newModel = 'iPhone 5 (GSM+CDMA)';
  else if (model.includes('iPhone5,3')) newModel = 'iPhone 5C (GSM)';
  else if (model.includes('iPhone5,4')) newModel = 'iPhone 5C (Global)';
  else if (model.includes('iPhone6,1')) newModel = 'iPhone 5S (GSM)';
  else if (model.includes('iPhone6,2')) newModel = 'iPhone 5S (Global)';
  else if (model.includes('iPhone7,1')) newModel = 'iPhone 6 Plus';
  else if (model.includes('iPhone7,2')) newModel = 'iPhone 6';
  else if (model.includes('iPhone8,1')) newModel = 'iPhone 6s';
  else if (model.includes('iPhone8,2')) newModel = 'iPhone 6s Plus';
  else if (model.includes('iPhone8,4')) newModel = 'iPhone SE (GSM)';
  else if (model.includes('iPhone9,1')) newModel = 'iPhone 7';
  else if (model.includes('iPhone9,2')) newModel = 'iPhone 7 Plus';
  else if (model.includes('iPhone9,3')) newModel = 'iPhone 7';
  else if (model.includes('iPhone9,4')) newModel = 'iPhone 7 Plus';
  else if (model.includes('iPhone10,1')) newModel = 'iPhone 8';
  else if (model.includes('iPhone10,2')) newModel = 'iPhone 8 Plus';
  else if (model.includes('iPhone10,3')) newModel = 'iPhone X Global';
  else if (model.includes('iPhone10,4')) newModel = 'iPhone 8';
  else if (model.includes('iPhone10,5')) newModel = 'iPhone 8 Plus';
  else if (model.includes('iPhone10,6')) newModel = 'iPhone X GSM';
  else if (model.includes('iPhone11,2')) newModel = 'iPhone XS';
  else if (model.includes('iPhone11,4')) newModel = 'iPhone XS Max';
  else if (model.includes('iPhone11,6')) newModel = 'iPhone XS Max Global';
  else if (model.includes('iPhone11,8')) newModel = 'iPhone XR';
  else if (model.includes('iPhone12,1')) newModel = 'iPhone 11';
  else if (model.includes('iPhone12,3')) newModel = 'iPhone 11 Pro';
  else if (model.includes('iPhone12,5')) newModel = 'iPhone 11 Pro Max';
  else if (model.includes('iPhone12,8')) newModel = 'iPhone SE 2nd Gen';
  else if (model.includes('iPhone13,1')) newModel = 'iPhone 12 Mini';
  else if (model.includes('iPhone13,2')) newModel = 'iPhone 12';
  else if (model.includes('iPhone13,3')) newModel = 'iPhone 12 Pro';
  else if (model.includes('iPhone13,4')) newModel = 'iPhone 12 Pro Max';
  else if (model.includes('iPhone14,2')) newModel = 'iPhone 13 Pro';
  else if (model.includes('iPhone14,3')) newModel = 'iPhone 13 Pro Max';
  else if (model.includes('iPhone14,4')) newModel = 'iPhone 13 Mini';
  else if (model.includes('iPhone14,5')) newModel = 'iPhone 13';
  else if (model.includes('iPhone14,6')) newModel = 'iPhone SE 3rd Gen';
  else if (model.includes('iPhone14,7')) newModel = 'iPhone 14';
  else if (model.includes('iPhone14,8')) newModel = 'iPhone 14 Plus';
  else if (model.includes('iPhone15,2')) newModel = 'iPhone 14 Pro';
  else if (model.includes('iPhone15,3')) newModel = 'iPhone 14 Pro Max';
  else if (model.includes('iPhone15,4')) newModel = 'iPhone 15';
  else if (model.includes('iPhone15,5')) newModel = 'iPhone 15 Plus';
  else if (model.includes('iPhone16,1')) newModel = 'iPhone 15 Pro';
  else if (model.includes('iPhone16,2')) newModel = 'iPhone 15 Pro Max';

  return newModel;
}

export function getCircleData(rate: any, isClass: boolean, type: string) {
  const femaleGoldCount =
    rate.femaleGoldCount !== null ? rate.femaleGoldCount : 0;
  const maleGoldCount = rate.maleGoldCount !== null ? rate.maleGoldCount : 0;
  const femaleSilverCount =
    rate.femaleSilverCount !== null ? rate.femaleSilverCount : 0;
  const maleSilverCount =
    rate.maleSilverCount !== null ? rate.maleSilverCount : 0;
  const femaleBronzeCount =
    rate.femaleBronzeCount !== null ? rate.femaleBronzeCount : 0;
  const maleBronzeCount =
    rate.maleBronzeCount !== null ? rate.maleBronzeCount : 0;

  const toFixed1 = (num: number) => parseFloat(num.toFixed(1));

  const goldAvg = toFixed1(
    rate.avgMemberCount
      ? ((femaleGoldCount + maleGoldCount) / rate.avgMemberCount) * 100
      : 0
  );
  const silverAvg = toFixed1(
    rate.avgMemberCount
      ? ((femaleSilverCount + maleSilverCount) / rate.avgMemberCount) * 100
      : 0
  );
  const bronzeAvg = toFixed1(
    rate.avgMemberCount
      ? ((femaleBronzeCount + maleBronzeCount) / rate.avgMemberCount) * 100
      : 0
  );
  let emptyAvg = toFixed1(100.0 - goldAvg - silverAvg - bronzeAvg);

  // 보정: 음수 방지 (소수점 오차로 인해 발생 가능)
  if (emptyAvg < 0) emptyAvg = 0;

  const goldSum = toFixed1(
    rate.sumMemberCount
      ? ((femaleGoldCount + maleGoldCount) / rate.sumMemberCount) * 100
      : 0
  );
  const silverSum = toFixed1(
    rate.sumMemberCount
      ? ((femaleSilverCount + maleSilverCount) / rate.sumMemberCount) * 100
      : 0
  );
  const bronzeSum = toFixed1(
    rate.sumMemberCount
      ? ((femaleBronzeCount + maleBronzeCount) / rate.sumMemberCount) * 100
      : 0
  );
  const emptySum = toFixed1(100.0 - goldSum - silverSum - bronzeSum);

  const result = [
    {
      medal: 'gold',
      value: femaleGoldCount + maleGoldCount,
      avg: isClass ? goldAvg : goldSum,
      sum: goldSum,
    },
    {
      medal: 'silver',
      value: femaleSilverCount + maleSilverCount,
      avg: isClass ? silverAvg : silverSum,
      sum: silverSum,
    },
    {
      medal: 'bronze',
      value: femaleBronzeCount + maleBronzeCount,
      avg: isClass ? bronzeAvg : bronzeSum,
      sum: bronzeSum,
    },
    {
      medal: 'empty',
      value:
        isClass && type === KEY_READ_TEXT
          ? Math.round((rate.avgMemberCount * emptyAvg) / 100)
          : Math.round((rate.sumMemberCount * emptySum) / 100),
      avg: isClass ? emptyAvg : emptySum,
      sum: emptySum,
    },
  ];

  return result;
}

export function getStackedBarData(rate: any) {
  const femaleGoldCount =
    rate.femaleGoldCount !== null ? rate.femaleGoldCount : 0;
  const maleGoldCount = rate.maleGoldCount !== null ? rate.maleGoldCount : 0;
  const femaleSilverCount =
    rate.femaleSilverCount !== null ? rate.femaleSilverCount : 0;
  const maleSilverCount =
    rate.maleSilverCount !== null ? rate.maleSilverCount : 0;
  const femaleBronzeCount =
    rate.femaleBronzeCount !== null ? rate.femaleBronzeCount : 0;
  const maleBronzeCount =
    rate.maleBronzeCount !== null ? rate.maleBronzeCount : 0;

  const totalAmount =
    femaleGoldCount +
    maleGoldCount +
    femaleSilverCount +
    maleSilverCount +
    femaleBronzeCount +
    maleBronzeCount;
  const maleAmount = maleGoldCount + maleSilverCount + maleBronzeCount;
  const femaleAmount = femaleGoldCount + femaleSilverCount + femaleBronzeCount;

  let gold =
    totalAmount !== 0
      ? Math.round(((femaleGoldCount + maleGoldCount) / totalAmount) * 100)
      : 0;
  let silver =
    totalAmount !== 0
      ? Math.round(((femaleSilverCount + maleSilverCount) / totalAmount) * 100)
      : 0;
  let bronze =
    totalAmount !== 0
      ? Math.round(((femaleBronzeCount + maleBronzeCount) / totalAmount) * 100)
      : 0;
  const total = gold + silver + bronze;
  if (total !== 100) {
    const excess = Math.round(total - 100);
    if (excess > 0) {
      if (gold >= silver && gold >= bronze) {
        gold -= excess;
      } else if (silver >= gold && silver >= bronze) {
        silver -= excess;
      } else {
        bronze -= excess;
      }
    }
  }

  let mGold =
    maleAmount !== 0 ? Math.round((maleGoldCount / maleAmount) * 100) : 0;
  let mSilver =
    maleAmount !== 0 ? Math.round((maleSilverCount / maleAmount) * 100) : 0;
  let mBronze =
    maleAmount !== 0 ? Math.round((maleBronzeCount / maleAmount) * 100) : 0;

  const mTotal = mGold + mSilver + mBronze;
  if (mTotal !== 100) {
    const excess = Math.round(mTotal - 100);
    if (excess > 0) {
      if (mGold >= mSilver && mGold >= mBronze) {
        mGold -= excess;
      } else if (mSilver >= mGold && mSilver >= mBronze) {
        mSilver -= excess;
      } else {
        mBronze -= excess;
      }
    }
  }

  let fGold =
    femaleAmount !== 0 ? Math.round((femaleGoldCount / femaleAmount) * 100) : 0;
  let fSilver =
    femaleAmount !== 0
      ? Math.round((femaleSilverCount / femaleAmount) * 100)
      : 0;
  let fBronze =
    femaleAmount !== 0
      ? Math.round((femaleBronzeCount / femaleAmount) * 100)
      : 0;
  const fTotal = Math.round(fGold + fSilver + fBronze);
  if (fTotal !== 100) {
    const excess = Math.round(fTotal - 100);
    if (excess > 0) {
      if (fGold >= fSilver && fGold >= fBronze) {
        fGold -= excess;
      } else if (fSilver >= fGold && fSilver >= fBronze) {
        fSilver -= excess;
      } else {
        fBronze -= excess;
      }
    }
  }
  const result = [
    {
      name: '전체',
      gold: gold,
      silver: silver,
      bronze: bronze,
      amt: totalAmount,
    },
    {
      name: '남자',
      gold: mGold,
      silver: mSilver,
      bronze: mBronze,
      amt: maleAmount,
    },
    {
      name: '여자',
      gold: fGold,
      silver: fSilver,
      bronze: fBronze,
      amt: femaleAmount,
    },
  ];
  return result;
}

export function shortenSchoolName(name: string) {
  return name
    .replace('초등학교', '초')
    .replace('중학교', '중')
    .replace('고등학교', '고');
}

export const getEffectiveDaysInMonth = (startDate: number, month: string) => {
  const targetDate = new Date(getYear(startDate * 1000), Number(month) - 1, 1);
  const today = new Date();

  // 현재 월과 비교
  if (isSameMonth(targetDate, today)) {
    // 현재 달이면 오늘 날짜까지
    return today.getDate();
  }

  // 그 외 달이면 해당 달의 전체 날짜 수
  return getDaysInMonth(targetDate);
};
